import { useState, useRef, useEffect, useCallback } from 'react'
import { Box, Input } from '@chakra-ui/react'
import { useFormColors } from '@minetrace/ui/forms'
import { DayPicker } from 'react-day-picker'
import { es } from 'react-day-picker/locale'
import 'react-day-picker/style.css'

interface FormDatePickerProps {
  value: string // "YYYY-MM-DD" or ""
  onChange: (date: string) => void
  placeholder?: string
  disabled?: boolean
  minDate?: string // "YYYY-MM-DD" — dates before this are disabled
  maxDate?: string // "YYYY-MM-DD" — dates after this are disabled
}

function formatDisplay(isoDate: string): string {
  if (!isoDate) return ''
  const [y, m, d] = isoDate.split('-')
  if (!y || !m || !d) return ''
  return `${d}/${m}/${y}`
}

function toDate(isoDate: string): Date | undefined {
  if (!isoDate) return undefined
  const d = new Date(isoDate + 'T00:00:00')
  return isNaN(d.getTime()) ? undefined : d
}

function toIso(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export default function FormDatePicker({ value, onChange, placeholder = 'Seleccionar fecha', disabled, minDate, maxDate }: FormDatePickerProps) {
  const c = useFormColors()
  const [open, setOpen] = useState(false)
  const [dropUp, setDropUp] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selected = toDate(value)

  const computeDropUp = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const calendarHeight = 340
      const spaceBelow = window.innerHeight - rect.bottom
      return spaceBelow < calendarHeight && rect.top > calendarHeight
    }
    return false
  }, [])

  const handleOpen = useCallback(() => {
    if (disabled) return
    setDropUp(computeDropUp())
    setOpen((prev) => !prev)
  }, [disabled, computeDropUp])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <Box ref={containerRef} position="relative">
      <Box position="relative">
        <Input
          readOnly
          value={formatDisplay(value)}
          placeholder={placeholder}
          onClick={handleOpen}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          disabled={disabled}
          pr="2.5rem"
        />
        <Box
          position="absolute" right="0.75rem" top="50%" transform="translateY(-50%)"
          pointerEvents="none" color={c.textDimmed}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </Box>
      </Box>
      {open && (
        <Box
          position="absolute"
          {...(dropUp ? { bottom: '100%', mb: 1 } : { top: '100%', mt: 1 })}
          left={0}
          zIndex={50}
          bg={c.bgCard}
          borderRadius="sm"
          boxShadow="lg"
          p={3}
          border="1px solid"
          borderColor={c.border}
        >
          <style>{`
            .minetrace-datepicker .rdp-root {
              --rdp-accent-color: ${c.accent};
              --rdp-accent-background: ${c.accent}20;
              --rdp-day-height: 36px;
              --rdp-day-width: 36px;
              font-family: inherit;
              font-size: 0.875rem;
            }
            .minetrace-datepicker .rdp-day button {
              border-radius: 0.5rem;
              color: ${c.text};
            }
            .minetrace-datepicker .rdp-day button:hover {
              background: ${c.bgInput};
            }
            .minetrace-datepicker .rdp-selected .rdp-day_button {
              background: ${c.accent} !important;
              color: white !important;
            }
            .minetrace-datepicker .rdp-caption_label,
            .minetrace-datepicker .rdp-month_caption {
              color: ${c.text};
              font-weight: 600;
            }
            .minetrace-datepicker .rdp-weekday {
              color: ${c.textMuted};
              font-size: 0.75rem;
            }
            .minetrace-datepicker .rdp-button_previous,
            .minetrace-datepicker .rdp-button_next {
              color: ${c.textSecondary};
            }
            .minetrace-datepicker .rdp-outside .rdp-day_button {
              color: ${c.textMuted};
              opacity: 0.5;
            }
            .minetrace-datepicker .rdp-today .rdp-day_button {
              font-weight: 700;
              color: ${c.accent};
            }
            .minetrace-datepicker .rdp-dropdowns {
              display: flex;
              gap: 0.5rem;
              align-items: center;
            }
            .minetrace-datepicker .rdp-dropdown_root { position: relative; }
            .minetrace-datepicker .rdp-dropdown {
              background: ${c.bgInput};
              color: ${c.text};
              border: 1px solid ${c.border};
              border-radius: 0.375rem;
              padding: 0.25rem 0.5rem;
              font-size: 0.875rem;
              font-weight: 600;
              cursor: pointer;
              appearance: none;
            }
            .minetrace-datepicker .rdp-dropdown:focus {
              outline: 2px solid ${c.accent};
              outline-offset: 1px;
            }
          `}</style>
          <div className="minetrace-datepicker">
            <DayPicker
              mode="single"
              captionLayout="dropdown"
              startMonth={minDate ? new Date(minDate + 'T00:00:00') : new Date(1940, 0)}
              endMonth={maxDate ? new Date(maxDate + 'T00:00:00') : new Date(new Date().getFullYear() + 10, 11)}
              selected={selected}
              onSelect={(date) => {
                if (date) {
                  onChange(toIso(date))
                } else {
                  onChange('')
                }
                setOpen(false)
              }}
              defaultMonth={selected}
              locale={es}
              disabled={[
                ...(minDate ? [{ before: new Date(minDate + 'T00:00:00') }] : []),
                ...(maxDate ? [{ after: new Date(maxDate + 'T00:00:00') }] : []),
              ]}
            />
          </div>
        </Box>
      )}
    </Box>
  )
}
